import React, { useEffect, useState, useContext } from "react"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"

import Divider from "../../../components/Divider"
import PodcastSignupBar from "../../../components/PodcastSignupBar"
import { GlobalDataContext } from "../../../layouts"
import { navigate } from "gatsby-link"
import { sortBy } from "lodash"
import styled from "styled-components"
import { graphql, Link } from "gatsby"
import { MetaTagsContext } from "../../../layouts"

export const ClipGrid = ({
  episodes,
  episode,
  clips,
  currentClipId,
  handleClick,
  className,
}) => {
  const IframeWrapper = styled.div`
    &::after {
      content: "";
      z-index: 9999;
      position: absolute;
      top: 0;
      bottom: 0;
      width: 100%;
    }
  `

  const isEpisodesListView = !!episodes
  const isSingleEpisodeView = !episodes && !!clips

  return (
    <div
      className={`grid grid-cols-1 items-start md:grid-cols-2 lg:grid-cols-3 gap-10 ${className}`}
    >
      {isEpisodesListView &&
        episodes.map((episode) => {
          const firstEpisodeClip = episode.podcastEpisodeClips[0]
          const episodeHasThumbnail = !!episode.clipThumbnail

          return (
            <div class="group">
              <Divider />

              <div
                className="pt-4 truncate
                md:pt-6
                lg:pt-8
                "
              >
                <Link
                  to={`/the-product-market-fit-show/episodes/${episode.urlPath}`}
                  className="text-36px font-light group-hover:underline 
                  lg:text-2-5xl"
                >
                  {`E${episode.episodeNumber}. ${episode.title}`}
                </Link>
              </div>

              <div
                className="pb-4 truncate
                md:pb-6
                lg:pb-8"
              >
                <Link
                  to={`/the-product-market-fit-show/episodes/${episode.urlPath}`}
                  className="text-18px lg:font-light lg:text-24px "
                >{`${episode.founderName}, ${episode.companyName}`}</Link>
              </div>

              <IframeWrapper
                onClick={() =>
                  navigate(
                    `/the-product-market-fit-show/episodes/${episode.urlPath}`
                  )
                }
                className="relative overflow-hidden cursor-pointer"
                style={{
                  paddingTop: "100%",
                  // The padding top shapes the aspect ratio box. Different sized videos would require an appropriate
                  // dynamic percentage. If all the videos have different aspect ratios this singular percentage wouldn't work
                  // Could add meta-data to the videos, like landscape or portrait
                }}
              >
                {episodeHasThumbnail && (
                  <div
                    className="absolute w-full h-full top-0 left-0 bg-center bg-contain"
                    style={{
                      backgroundImage: `url(${episode.clipThumbnail.thumbnail.url})`,
                    }}
                  />
                )}

                {!episodeHasThumbnail && (
                  <iframe
                    className="absolute w-full h-full top-0 left-0 border-0"
                    src={`https://player.vimeo.com/video/${firstEpisodeClip.vimeoVideoId}?h=63e72e33f7&title=1&byline=0&portrait=0&controls=0`}
                    frameborder="0"
                  />
                )}
              </IframeWrapper>
            </div>
          )
        })}

      {isSingleEpisodeView &&
        clips.map((clip, index) => {
          const clipHasAThumbnail = clip.thumbnail

          return (
            clip.id !== currentClipId && (
              <div class="group">
                <Divider />

                <div
                  className="py-4 md:py-6 lg:py-8 text-36px font-light truncate group-hover:underline cursor-pointer
                 lg:text-2-5xl
                "
                >
                  {clip.title}
                </div>

                <IframeWrapper
                  onClick={() => handleClick(clip, index)}
                  className="relative overflow-hidden cursor-pointer"
                  style={{
                    paddingTop: "100%",
                    // The padding top shapes the aspect ratio box. Different sized videos would require an appropriate
                    // dynamic percentage. If all the videos have different aspect ratios this singular percentage wouldn't work
                    // Could add meta-data to the videos, like landscape or portrait
                  }}
                >
                  {clipHasAThumbnail && (
                    <div
                      className="absolute w-full h-full top-0 left-0 bg-center bg-contain"
                      style={{
                        backgroundImage: `url(${clip.thumbnail.url})`,
                      }}
                    />
                  )}

                  {!clipHasAThumbnail && (
                    <iframe
                      className="absolute w-full h-full top-0 left-0 border-0"
                      src={`https://player.vimeo.com/video/${clip.vimeoVideoId}?h=63e72e33f7&title=1&byline=0&portrait=0&controls=0`}
                      frameborder="0"
                    />
                  )}
                </IframeWrapper>
              </div>
            )
          )
        })}
    </div>
  )
}

const Component = ({ data, location }) => {
  const episodeData = data.strapiPodcastEpisodes
  const globalData = React.useContext(GlobalDataContext)

  const [isClipPlaying, setIsClipPlaying] = useState(false)
  const [sortedClips, setSortedClips] = useState(null)
  const [currentClip, setCurrentClip] = useState(null)
  const [currentClipArrayIndex, setCurrentClipArrayIndex] = useState(null)

  const { setMetaTags } = useContext(MetaTagsContext)
  setMetaTags(data.strapiPodcastEpisodes.seo)

  const adjustCurrentClip = (clip, index) => {
    const videoElement = document.documentElement

    setCurrentClip(clip)
    setIsClipPlaying(true)
    videoElement.scrollIntoView({
      behavior: "smooth",
    })
    setCurrentClipArrayIndex(index)
  }

  const goToPreviousClip = () => {
    const newIndex = currentClipArrayIndex - 1
    const previousClip = sortedClips[newIndex]

    setCurrentClip(previousClip)
    setCurrentClipArrayIndex(newIndex)
  }

  const goToNextClip = () => {
    const newIndex = currentClipArrayIndex + 1
    const nextClip = sortedClips[newIndex]

    setCurrentClip(nextClip)
    setCurrentClipArrayIndex(newIndex)
  }

  useEffect(() => {
    if (sortedClips) {
      setCurrentClip(sortedClips[0])
      setCurrentClipArrayIndex(0)
    }
  }, [sortedClips])

  useEffect(() => {
    if (episodeData) {
      setSortedClips(sortBy(episodeData.podcastEpisodeClips, "clipNumber"))
    }

    const routeHash = location.hash.substring(1)
    const transcriptElement = document.getElementById("transcript")
    if (routeHash.toUpperCase() === "TRANSCRIPT" && transcriptElement) {
      setTimeout(() => {
        transcriptElement.scrollIntoView({
          behavior: "smooth",
        })
      }, 1000)
    }
  }, [episodeData])

  const CurrentClip = () => {
    const isFirstClip =
      sortedClips &&
      currentClip &&
      sortedClips[0].clipNumber === currentClip.clipNumber
    const isLastClip =
      sortedClips &&
      currentClip &&
      sortedClips[sortedClips.length - 1].clipNumber === currentClip.clipNumber

    return (
      <>
        {/* Video */}
        <div className="lg:hidden">
          <h1 className="pt-10 font-light text-36px leading-tight">{`E${
            episodeData.episodeNumber
          }. ${currentClip && episodeData.title}`}</h1>
          <h2 className="font-light text-24px tracking-wide pb-2">
            {`${currentClip && currentClip.title}`}
          </h2>
          <h2 className="text-18px tracking-wide pb-8">
            {`${episodeData.founderName}, ${episodeData.companyName}`}
          </h2>
          {currentClip && (
            <>
              <div id="current-clip" className="relative mb-6 pt-videos">
                <iframe
                  src={`https://player.vimeo.com/video/${currentClip.vimeoVideoId}?h=63e72e33f7&title=0&byline=0&portrait=0`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  frameborder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                ></iframe>
              </div>
              <script src="https://player.vimeo.com/api/player.js"></script>
            </>
          )}
          <div className="flex justify-between pt-4 pb-8">
            <div className="col-span-2 text-center uppercase tracking-widest text-sm">
              {!isFirstClip && (
                <div className="cursor-pointer" onClick={goToPreviousClip}>
                  &lt; {globalData.podcastEpisodePreviousClip}
                </div>
              )}
            </div>
            <div className="col-span-2 text-center uppercase tracking-widest text-sm">
              {!isLastClip && (
                <div className="cursor-pointer" onClick={goToNextClip}>
                  {globalData.podcastEpisodeNextClip} &gt;
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Video */}

        {/* Large Video */}
        <div className="hidden lg:grid grid-cols-12 py-16 items-center">
          <div className="col-span-2"></div>
          <div className="col-span-10">
            <h1 className="font-light text-4-5xl leading-tight">{`E${
              episodeData.episodeNumber
            }. ${currentClip && episodeData.title}`}</h1>
            <h2 className="font-light text-24px tracking-wide pb-2">
              {`${currentClip && currentClip.title}`}
            </h2>
            <h2 className="text-xl tracking-wide pb-8">
              <div>{`${episodeData.founderName}, ${episodeData.companyName}`}</div>
            </h2>
          </div>

          <div className="col-span-2 pr-8 text-right uppercase tracking-widest text-sm">
            {!isFirstClip && (
              <div className="cursor-pointer" onClick={goToPreviousClip}>
                &lt; {globalData.podcastEpisodePreviousClip}
              </div>
            )}
          </div>
          <div className="col-span-8">
            {currentClip && (
              <>
                <div
                  id="current-clip"
                  className="relative mt-10 mb-6 pt-videos"
                >
                  <iframe
                    src={`https://player.vimeo.com/video/${currentClip.vimeoVideoId}?h=63e72e33f7&title=0&byline=0&portrait=0`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                </div>
                <script src="https://player.vimeo.com/api/player.js"></script>
              </>
            )}
          </div>
          <div className="col-span-2 pl-8 uppercase tracking-widest text-sm">
            {!isLastClip && (
              <div className="cursor-pointer" onClick={goToNextClip}>
                {globalData.podcastEpisodeNextClip} &gt;
              </div>
            )}
          </div>
        </div>
        {/* Large Video */}
      </>
    )
  }

  return (
    <>
      {episodeData && (
        <div className="bg-white">
          <div className="container">
            <CurrentClip />

            <ClipGrid
              episode={episodeData}
              clips={sortedClips}
              currentClipId={currentClip && currentClip.id}
              className="pb-8 lg:pb-16"
              handleClick={adjustCurrentClip}
            />
          </div>

          <PodcastSignupBar />

          <div className="container">
            <div className="lg:grid lg:grid-cols-12 py-8 lg:py-16">
              <div className="hidden lg:block lg:col-span-2"></div>
              <div className="lg:col-span-8">
                <Divider />

                <div id="transcript" className="pb-6 lg:pt-8 lg:pb-16">
                  <div className="pt-4 pb-6 text-36px font-light lg:font-bold">
                    {globalData.podcastTranscriptLabel}
                  </div>
                  <div className="text-18px lg:text-xl">
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {episodeData.transcript}
                    </ReactMarkdown>
                  </div>
                </div>

                <Divider className="hidden lg:block" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export const query = graphql`
  query ($id: String) {
    strapiPodcastEpisodes(id: { eq: $id }) {
      episodeNumber
      title
      urlPath
      founderName
      companyName
      transcript
      seo {
        title
        description
      }
      clipThumbnail {
        thumbnail {
          url
        }
      }
      podcastEpisodeClips {
        id
        clipNumber
        podcastEpisode
        title
        vimeoVideoId
        thumbnail {
          url
        }
      }
    }
  }
`

export default Component
